import React, { memo, useState, useRef, useCallback, useEffect, useContext, forwardRef, useImperativeHandle, useMemo, SyntheticEvent } from 'react';
import { CalendarProvider, DateData, ExpandableCalendar, Timeline, TimelineList } from 'react-native-calendars';
import { DATE_SELECTED } from '@reduxLocal/actions/types';
import { store } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { View, Text } from '@constants/Themed';
import { Platform, useWindowDimensions, Pressable, Image, Modal } from 'react-native';
import { backgroundColor } from 'react-native-calendars/src/style';
import CalendarDayModal, { CalendarDayModalRef } from './CalendarDayModal';
import apis from '@api/api';
import { ToolbarContext } from '@hooks/toolbarContext';
import { LinearGradient } from 'expo-linear-gradient';
import { CalendarContextProps } from 'react-native-calendars/src/expandableCalendar/Context';
import Button from '@components/UI/Button/Button';
import moment from 'moment';
import * as Animatable from 'react-native-animatable';
import AddCalendarEntry from './addCalendarEntry';
import { H1, Label, ScrollView, Separator, SizableText, TextArea, XStack, YStack, Dialog, Sheet } from 'tamagui';
import { Popover } from 'tamagui';
import ErrorBoundary from '@utils/ErrorBoundary';
import { useDispatch } from 'react-redux';
import LottieView from '@utils/lottieAnimation';
import { uniqueId } from 'lodash';
import { useDeviceType } from '@hooks/useDeviceType';
import { SocialEventContent, SocialEventMarking } from './subcomponents/SocialEvent';

// Helper function to get all dates between two dates
const getDateRange = (startDate: string, endDate: string): string[] => {
  const dates: string[] = [];
  const start = moment(startDate);
  const end = moment(endDate);

  // Ensure start is before end
  const [earlier, later] = start.isBefore(end) ? [start, end] : [end, start];

  while (earlier.isSameOrBefore(later, 'day')) {
    dates.push(earlier.format('YYYY-MM-DD'));
    earlier.add(1, 'day');
  }

  return dates;
};

// Helper function to check if a date is in range
const isDateInRange = (date: string, startDate: string | null, endDate: string | null): boolean => {
  if (!startDate || !endDate) return false;
  const dateRange = getDateRange(startDate, endDate);
  return dateRange.includes(date);
};

// Helper functions for user avatars
const correctStorageUrl = (url: string | undefined) => {
  if (!url) return null;
  return url.replace(
    'https://storage.googleapis.com/smartsaas-avatar-store/',
    'https://storage.cloud.google.com/smartsaas-avatar-store/'
  );
};

const getUserDisplayName = (user: any) => {
  if (!user) return null;
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  if (user.firstName) return user.firstName;
  if (user.lastName) return user.lastName;
  if (user.email) return user.email;
  return 'Unknown User';
};

// User Avatar Component
const UserAvatar = ({ user, size = 16 }: { user?: any; size?: number }) => {
  if (!user) return null;

  const avatarUrl = correctStorageUrl(user.avatar);
  const userName = getUserDisplayName(user);

  if (avatarUrl) {
    return (
      <Image
        source={{ uri: avatarUrl }}
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          marginRight: 4,
          borderWidth: 1,
          borderColor: 'rgba(255, 255, 255, 0.5)',
        }}
        onError={() => console.log('Error loading avatar for', userName)}
      />
    );
  }

  // Fallback to initials
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        marginRight: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.4)',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.5)',
      }}
    >
      <Text style={{ color: '#ffffff', fontSize: size * 0.5, fontWeight: '700' }}>
        {userName?.charAt(0).toUpperCase()}
      </Text>
    </View>
  );
};

type CalendarComponentProps = {
  dateSelected: string;
  calendarType: string;
  handleMarkSelection: (date: string) => void;
  onEventClick?: (event: any) => void;
  eventList: any;
  finalMarked: any;
  activeMonth?: string;
  setMonth: (date: string) => void;
  setCalendarType: (type: string) => void;
  calHeight?: number;
  calLoading: boolean;
  googleCal: () => void;
  dateCallback: (date: DateData) => void;
};

export interface CalendarHandle {
  setMonth: (date: string) => void;
  incrementMonth: () => void;
  decrementMonth: () => void;
  showAddGoogleCal: () => void;
}

const CalendarComponent = forwardRef<CalendarHandle, CalendarComponentProps>((props, ref) => {
  const { dateSelected, calHeight, googleCal, calendarType, handleMarkSelection, dateCallback, eventList, finalMarked, calLoading, onEventClick } = props;
  const { setCalendarContext } = useContext(ToolbarContext);
  const [isOpen, setIsOpen] = useState(true);
  const theme = themeSelector();
  const [selectedDate, setSelectedDate] = useState<DateData>(dateSelected);
  const calendarDayRef = useRef<CalendarDayModalRef>(null);
  const { width } = useWindowDimensions();
  const calendarHeight = calHeight;
  const calendarWidth = width;
  const dispatch = useDispatch();
  const { isTablet } = useDeviceType();

  // Drag selection state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartDate, setDragStartDate] = useState<string | null>(null);
  const [dragEndDate, setDragEndDate] = useState<string | null>(null);
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<string[]>([]);
  const [isBlockingDates, setIsBlockingDates] = useState(false);
  const [isCreatingMultiDayEvent, setIsCreatingMultiDayEvent] = useState(false);

  const getActiveCalendarId = useCallback(() => {
    const calendarState: any = store.getState()?.calendar;
    if (!calendarState) return null;

    const activeCalendars = Array.isArray(calendarState.activeCalendars) ? calendarState.activeCalendars : [];
    const availableCalendars = Array.isArray(calendarState.availableCalendars) ? calendarState.availableCalendars : [];
    const primaryCalendar = activeCalendars[0] || availableCalendars[0];

    if (!primaryCalendar) {
      return null;
    }

    return (
      primaryCalendar.calendarId ||
      primaryCalendar._id ||
      primaryCalendar.id ||
      primaryCalendar?.calendar?.id ||
      primaryCalendar?.source?.id ||
      null
    );
  }, []);

  useImperativeHandle(ref, () => ({
    setMonth: (date) => {
      setSelectedDate(moment(date).format('YYYY-MM-DD'));
    },
    incrementMonth: () => {
      setSelectedDate((prev) => {
        return moment(prev).add(1, 'month').format('YYYY-MM-DD');
      });
    },
    decrementMonth: () => {
      setSelectedDate((prev) => {
        return moment(prev).subtract(1, 'month').format('YYYY-MM-DD');
      });
    },
    showAddGoogleCal: () => {
      googleCal();
    }
  }), []);

  const _handleDayPress = useCallback((event, date) => {
    handleMarkSelection(date);
    setSelectedDate(date);
  }, [selectedDate]);

  useEffect(() => {
    setIsOpen(!isOpen);
  }, [theme])

  const _handleLongPress = useCallback((date) => {
    setSelectedDate?.(date.dateString);
    calendarDayRef.current?.openModal();
  }, [calendarDayRef]);



  const handleDrop = (e: SyntheticEvent) => {
    e.preventDefault();
    calendarDayRef.current?.openModal();
  }

  // Drag selection handlers
  const handleDragStart = useCallback((date: string) => {
    setIsDragging(true);
    setDragStartDate(date);
    setDragEndDate(date);
  }, []);

  const handleDragOver = useCallback((date: string) => {
    if (isDragging && dragStartDate) {
      setDragEndDate(date);
    }
  }, [isDragging, dragStartDate]);

  const handleDragEnd = useCallback(() => {
    if (isDragging && dragStartDate && dragEndDate) {
      const dateRange = getDateRange(dragStartDate, dragEndDate);
      setSelectedDateRange(dateRange);
      setShowDateRangeModal(true);
    }
    setIsDragging(false);
  }, [isDragging, dragStartDate, dragEndDate]);

  const handleCancelDragSelection = useCallback(() => {
    setShowDateRangeModal(false);
    setDragStartDate(null);
    setDragEndDate(null);
    setSelectedDateRange([]);
  }, []);

  const handleBlockDates = useCallback(async () => {
    if (selectedDateRange.length === 0) {
      return;
    }

    const firstDate = selectedDateRange[0];
    const lastDate = selectedDateRange[selectedDateRange.length - 1];
    const startDateISO = moment(firstDate).startOf('day').toISOString();
    const endDateISO = moment(lastDate).endOf('day').toISOString();
    const rangeLabel = selectedDateRange.length === 1
      ? moment(firstDate).format('MMM D, YYYY')
      : `${moment(firstDate).format('MMM D, YYYY')} → ${moment(lastDate).format('MMM D, YYYY')}`;

    const activeCalendarId = getActiveCalendarId();

    if (!activeCalendarId) {
      await apis.blockCalendarDates({
        calendarId: '',
        startDate: startDateISO,
        endDate: endDateISO,
        reason: `Blocked ${selectedDateRange.length} day${selectedDateRange.length === 1 ? '' : 's'} (${rangeLabel})`
      });
      return;
    }

    setIsBlockingDates(true);
    try {
      await apis.blockCalendarDates({
        calendarId: activeCalendarId,
        startDate: startDateISO,
        endDate: endDateISO,
        reason: `Blocked ${selectedDateRange.length} day${selectedDateRange.length === 1 ? '' : 's'} (${rangeLabel})`
      });
      handleCancelDragSelection();
    } catch (error) {
      console.error('Error blocking dates:', error);
    } finally {
      setIsBlockingDates(false);
    }
  }, [selectedDateRange, getActiveCalendarId, handleCancelDragSelection]);

  const handleCreateMultiDayEvent = useCallback(async () => {
    if (selectedDateRange.length === 0) {
      return;
    }

    const firstDate = selectedDateRange[0];
    const lastDate = selectedDateRange[selectedDateRange.length - 1];
    const startDateISO = moment(firstDate).startOf('day').toISOString();
    const endDateISO = moment(lastDate).endOf('day').toISOString();
    const eventTitle = selectedDateRange.length === 1
      ? `Event on ${moment(firstDate).format('MMM D, YYYY')}`
      : `Multi-day event (${selectedDateRange.length} days)`;

    const activeCalendarId = getActiveCalendarId();
    const payload = {
      calendarId: activeCalendarId || '',
      title: eventTitle,
      startDate: startDateISO,
      endDate: endDateISO,
      allDay: true
    };

    if (!activeCalendarId) {
      await apis.createMultiDayEvent(payload);
      return;
    }

    setIsCreatingMultiDayEvent(true);
    try {
      await apis.createMultiDayEvent(payload);
      setSelectedDate(firstDate);
      handleCancelDragSelection();
    } catch (error) {
      console.error('Error creating multi-day event:', error);
    } finally {
      setIsCreatingMultiDayEvent(false);
    }
  }, [selectedDateRange, getActiveCalendarId, handleCancelDragSelection]);

  // Add global mouse up listener for drag end
  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleGlobalMouseUp = () => {
        if (isDragging) {
          handleDragEnd();
        }
      };

      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [isDragging, handleDragEnd]);

  // Add ESC key handler to cancel drag selection
  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleEscKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          if (isDragging) {
            // Cancel ongoing drag
            setIsDragging(false);
            setDragStartDate(null);
            setDragEndDate(null);
          } else if (showDateRangeModal) {
            // Cancel modal
            handleCancelDragSelection();
          }
        }
      };

      window.addEventListener('keydown', handleEscKey);
      return () => window.removeEventListener('keydown', handleEscKey);
    }
  }, [isDragging, showDateRangeModal, handleCancelDragSelection]);


  // Date Range Selection Modal Component
  const DateRangeSelectionModal = () => (
    <Dialog modal open={showDateRangeModal} onOpenChange={setShowDateRangeModal}>
      <Dialog.Portal>
        <Dialog.Overlay
          key="overlay"
          animation="quick"
          opacity={0.5}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        />
        <Dialog.Content
          bordered
          elevate
          key="content"
          animateOnly={['transform', 'opacity']}
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          style={{
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(40, 40, 40, 0.98)',
            borderRadius: 24,
            padding: 32,
            maxWidth: 520,
            shadowRadius: 24,
            shadowOpacity: theme ? 0.15 : 0.35,
            shadowColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.6)',
            shadowOffset: { width: 0, height: 8 },
            borderWidth: 1,
            borderColor: theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.08)',
          }}
        >
          <YStack gap="$4">
            <Dialog.Title
              style={{
                fontSize: 24,
                fontWeight: '700',
                color: theme ? 'rgb(15, 23, 42)' : 'rgb(248, 250, 252)',
                fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
              }}
            >
              Date Range Selected
            </Dialog.Title>

            <Dialog.Description
              style={{
                fontSize: 15,
                color: theme ? 'rgb(71, 85, 105)' : 'rgb(203, 213, 225)',
                lineHeight: 22,
                fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
              }}
            >
              You've selected {selectedDateRange.length} day{selectedDateRange.length > 1 ? 's' : ''} from{' '}
              <Text style={{ fontWeight: '600', color: theme ? 'rgb(30, 41, 59)' : 'rgb(248, 250, 252)' }}>
                {moment(selectedDateRange[0]).format('MMM D, YYYY')}
              </Text>
              {selectedDateRange.length > 1 && (
                <>
                  {' '}to{' '}
                  <Text style={{ fontWeight: '600', color: theme ? 'rgb(30, 41, 59)' : 'rgb(248, 250, 252)' }}>
                    {moment(selectedDateRange[selectedDateRange.length - 1]).format('MMM D, YYYY')}
                  </Text>
                </>
              )}
            </Dialog.Description>

            <Separator backgroundColor={theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'} marginVertical="$2" />

            <YStack gap="$3">
              <Button
                onPress={handleCreateMultiDayEvent}
                disabled={isCreatingMultiDayEvent}
                customStyle={{
                  backgroundColor: 'rgb(59, 130, 246)',
                  paddingVertical: 14,
                  paddingHorizontal: 24,
                  borderRadius: 12,
                  shadowRadius: 8,
                  shadowOpacity: 0.15,
                  shadowColor: 'rgb(59, 130, 246)',
                  shadowOffset: { width: 0, height: 4 },
                  opacity: isCreatingMultiDayEvent ? 0.7 : 1,
                }}
              >
                <Text style={{
                  color: 'white',
                  fontWeight: '600',
                  fontSize: 15,
                  textAlign: 'center',
                  fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
                }}>
                  {isCreatingMultiDayEvent ? 'Creating...' : '📅 Create Multi-Day Event'}
                </Text>
              </Button>

              <Button
                onPress={handleBlockDates}
                disabled={isBlockingDates}
                customStyle={{
                  backgroundColor: theme ? 'rgba(239, 68, 68, 0.9)' : 'rgba(220, 38, 38, 0.9)',
                  paddingVertical: 14,
                  paddingHorizontal: 24,
                  borderRadius: 12,
                  shadowRadius: 8,
                  shadowOpacity: 0.15,
                  shadowColor: 'rgba(239, 68, 68, 0.5)',
                  shadowOffset: { width: 0, height: 4 },
                  opacity: isBlockingDates ? 0.7 : 1,
                }}
              >
                <Text style={{
                  color: 'white',
                  fontWeight: '600',
                  fontSize: 15,
                  textAlign: 'center',
                  fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
                }}>
                  {isBlockingDates ? 'Blocking...' : '🚫 Block Off Dates'}
                </Text>
              </Button>

              <Button
                onPress={handleCancelDragSelection}
                customStyle={{
                  backgroundColor: 'transparent',
                  paddingVertical: 14,
                  paddingHorizontal: 24,
                  borderRadius: 12,
                  borderWidth: 2,
                  borderColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                }}
              >
                <Text style={{
                  color: theme ? 'rgb(71, 85, 105)' : 'rgb(203, 213, 225)',
                  fontWeight: '600',
                  fontSize: 15,
                  textAlign: 'center',
                  fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
                }}>
                  Cancel
                </Text>
              </Button>
            </YStack>
          </YStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );

  return useMemo(() => (
    <View style={{ flex: 1 }}
    >
      {/* <AddCalendarxEntry ref={calendarEntry} /> */}
      <DateRangeSelectionModal />

      {/* Drag Selection Badge */}
      {isDragging && dragStartDate && dragEndDate && Platform.OS === 'web' && (
        <Animatable.View
          animation="zoomIn"
          duration={200}
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            backgroundColor: 'rgba(59, 130, 246, 0.95)',
            paddingHorizontal: 20,
            paddingVertical: 12,
            borderRadius: 12,
            zIndex: 1000,
            shadowRadius: 12,
            shadowOpacity: 0.3,
            shadowColor: 'rgba(0, 0, 0, 0.3)',
            shadowOffset: { width: 0, height: 4 },
            borderWidth: 2,
            borderColor: 'rgba(255, 255, 255, 0.3)',
          }}
        >
          <XStack gap="$2" alignItems="center">
            <Text style={{
              color: 'white',
              fontSize: 18,
              fontWeight: '700',
              fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
            }}>
              📅
            </Text>
            <YStack gap="$1">
              <Text style={{
                color: 'white',
                fontSize: 20,
                fontWeight: '800',
                fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
                lineHeight: 20,
              }}>
                {getDateRange(dragStartDate, dragEndDate).length}
              </Text>
              <Text style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: 11,
                fontWeight: '600',
                fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
                lineHeight: 11,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}>
                {getDateRange(dragStartDate, dragEndDate).length === 1 ? 'DAY' : 'DAYS'}
              </Text>
            </YStack>
          </XStack>
        </Animatable.View>
      )}

      <CalendarDayModal nullify={['id', 'system', 'originalStartDate', 'organizer', 'posted_by', 'calendarid', 'isDetached', 'creationDate', 'source_type']} ref={calendarDayRef} events={eventList} selectedDate={selectedDate} />
      <CalendarProvider
        date={selectedDate}
        key={selectedDate}
        disabledOpacity={0.6}
        timelineLeftInset={10}
        todayBottomMargin={120}
        style={{ 
          flex: 3, 
          padding: 24, 
          marginHorizontal: -12, 
          marginTop: 0, 
          alignSelf: 'stretch', 
          flexDirection: "row",
          borderRadius: 16,
        }}
        theme={{
          backgroundColor: 'transparent',
          calendarBackground: 'transparent',
          textSectionTitleColor: "transparent",
          selectedDayBackgroundColor: 'rgb(59, 130, 246)', // Modern blue-500
          selectedDayTextColor: 'white',
          todayTextColor: theme ? 'rgb(59, 130, 246)' : 'rgb(147, 197, 253)', // blue-500 / blue-300
          dayTextColor: theme ? 'rgb(30, 41, 59)' : 'rgb(248, 250, 252)', // slate-800 / slate-50
          textDisabledColor: theme ? 'rgb(148, 163, 184)' : 'rgb(71, 85, 105)', // slate-400 / slate-600
          dotColor: theme ? 'rgb(59, 130, 246)' : 'rgb(147, 197, 253)', // blue-500 / blue-300
          selectedDotColor: 'white',
          arrowColor: theme ? 'rgb(59, 130, 246)' : 'rgb(147, 197, 253)', // blue-500 / blue-300
          monthTextColor: theme ? 'rgb(15, 23, 42)' : 'rgb(248, 250, 252)', // slate-900 / slate-50
          indicatorColor: 'rgb(59, 130, 246)', // blue-500
          textDayFontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
          textMonthFontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
          textDayHeaderFontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
          textDayFontWeight: '500',
          textMonthFontWeight: '700',
          textDayHeaderFontWeight: '600',
          textDayFontSize: 14,
          textMonthFontSize: 18,
          textDayHeaderFontSize: 0,
          contentStyle: { backgroundColor: 'transparent' },
          // Modern web calendar styles
          ...(Platform.OS === 'web' && {
            'stylesheet.calendar.main': {
              container: {
                borderRadius: 16,
                backgroundColor: 'transparent',
              },
            },
            'stylesheet.calendar.header': {
              monthText: {
                fontSize: 20,
                fontWeight: '700',
                color: theme ? 'rgb(15, 23, 42)' : 'rgb(248, 250, 252)',
                fontFamily: 'Inter, system-ui, sans-serif',
                letterSpacing: '-0.025em',
              },
              arrow: {
                padding: 8,
                borderRadius: 8,
                backgroundColor: theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(147, 197, 253, 0.1)',
              },
            },
            'stylesheet.day.basic': {
              base: {
                borderRadius: 8,
                margin: 2,
                transition: 'all 0.2s ease',
              },
              today: {
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderColor: 'rgb(59, 130, 246)',
                borderWidth: 2,
                borderRadius: 8,
              },
              selected: {
                backgroundColor: 'rgb(59, 130, 246)',
                borderRadius: 8,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              },
            },
          }),
        }}
      >
        <ErrorBoundary>
        <ExpandableCalendar
          onMonthChange={(date: DateData) => {
            dispatch({ type: DATE_SELECTED, payload: date.dateString });
            setCalendarContext(moment(date.dateString).format('MMMM YYYY'));
            dateCallback(date);
          }}
          firstDay={1}
          initialDate={typeof selectedDate === 'string' ? selectedDate : selectedDate.dateString}
          key={typeof selectedDate === 'string' ? selectedDate : selectedDate.dateString}
          enableSwipeMonths={false}
          animateScroll
          disableIntervalMomentum
          alwaysBounceHorizontal
          alwaysBounceVertical
          centerContent
          dayComponent={({ date, state, marking, markingType, onPress, onLongPress }) => {
           const [isHovered, setIsHovered] = useState(false);
           const eventInfo = eventList.filter(event => event.startDate.split('T')[0] === date.dateString);
           const isBefore = () => date.dateString < new Date().toISOString().split('T')[0];
           const [draggedOver, setDraggedOver] = useState(false);
           const isInDragRange = isDateInRange(date.dateString, dragStartDate, dragEndDate);
           const hasBlockedEvent = eventInfo.some((event) => event.title && event.title.includes('Blocked'));

            const handleDragOverEvent = (e: SyntheticEvent) => {
              if (isDragging) return;
              e.preventDefault();
              setDraggedOver(true);
            }

            const handleDragLeaveEvent = (e: SyntheticEvent) => {
              if (isDragging) return;
              e.preventDefault();
              setDraggedOver(false);
            }

            const handleMouseDown = (e: any) => {
              if (Platform.OS === 'web') {
                e.preventDefault();
                handleDragStart(date.dateString);
              }
            };

            const handleMouseEnter = () => {
              if (Platform.OS === 'web' && isDragging) {
                handleDragOver(date.dateString);
              }
            };

            return useMemo(() => (
              <div
                onDragOver={handleDragOverEvent}
                onDragLeave={handleDragLeaveEvent}
                onDrop={handleDrop}
                onMouseDown={handleMouseDown}
                onMouseEnter={handleMouseEnter}
                style={{ 
                  flex: 1, 
                  borderRadius: 8, 
                  overflow: "hidden", 
                  display: "flex", 
                  alignSelf: "stretch", 
                  flexDirection: "column", 
                  minHeight: calendarHeight / 8,
                  transition: 'all 0.2s ease',
                  border: state === 'today' ? '2px solid rgb(59, 130, 246)' : 'none',
                  backgroundColor: isInDragRange
                    ? 'rgba(59, 130, 246, 0.25)'
                    : draggedOver
                      ? 'rgba(59, 130, 246, 0.1)'
                      : 'transparent',
                  cursor: isDragging ? 'crosshair' : 'pointer',
                  userSelect: 'none',
                }}
              >
                <ErrorBoundary>
                  <LinearGradient 
                    locations={isBefore() ? [0.5, 0.51] : undefined} 
                    start={{ x: 1, y: 0 }} 
                    colors={
                      state === 'today' 
                        ? ['rgba(59, 130, 246, 0.1)', 'rgba(59, 130, 246, 0.05)'] 
                        : [
                            isBefore() 
                              ? (theme ? 'rgba(148, 163, 184, 0.1)' : 'rgba(71, 85, 105, 0.1)') 
                              : 'transparent', 
                            'transparent'
                          ]
                    } 
                    style={{ 
                      flex: 1, 
                      alignSelf: "stretch", 
                      overflow: "hidden", 
                      minHeight: "110%", 
                      borderRadius: 8 
                    }}
                  >
                    <Pressable 
                      onLongPress={() => onLongPress(date)} 
                      onPress={() => onPress(date)} 
                      style={{ 
                        right: 0, 
                        padding: 12, 
                        borderRadius: 8,
                      }}
                    >
                        {({ hovered }) => (
                          <>
                          <Animatable.Text 
                            transition={'color'} 
                            style={{ 
                              userSelect: "none", 
                              fontWeight: "600", 
                              textAlign: "right", 
                              fontSize: 14,
                              color: hovered || (selectedDate as any) === date.dateString 
                                ? (date.dateString === moment().format('YYYY-MM-DD') ? 'white' : 'rgb(59, 130, 246)') 
                                : (theme ? "rgb(30, 41, 59)" : "rgb(248, 250, 252)"),
                              fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
                            }}
                          >
                            {date.day}
                          </Animatable.Text>
                            {
                            (selectedDate as any) === date.dateString &&
                              <LottieView source={require("@assets/animations/mark-circle.json")} autoPlay style={{ position: "absolute", right: -7, top: 5, minHeight: 40, minWidth: 40, maxHeight: 40, maxWidth: 40 }} />
                            }
                            {
                              marking?.periods?.length > 3 &&
                            <Animatable.Text 
                              transition={'color'} 
                              style={{ 
                                userSelect: "none", 
                                fontWeight: "600", 
                                textAlign: "left", 
                                position: "absolute", 
                                left: 12, 
                                fontSize: 12,
                                color: isHovered ? 'rgb(59, 130, 246)' : (theme ? "rgb(71, 85, 105)" : "rgb(148, 163, 184)"),
                                fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
                              }}
                            >
                              {marking?.periods.length}
                            </Animatable.Text>
                            }
                          </>
                        )}
                      </Pressable>
                    <Animatable.View useNativeDriver transition={'backgroundColor'} style={{ flex: 1, alignSelf: "stretch", backgroundColor: draggedOver ? "blue" : "transparent" }}>
                      <ScrollView contentContainerStyle={{ gap: 2 }} showsVerticalScrollIndicator={false} style={{ maxHeight: 75, paddingTop: 0, gap: 2 }}>
                          {markingType === 'multi-period' && marking?.marked && marking?.periods.map((dot, index) => {
                            const length = marking?.periods.length;
                            return (
                              <Popover
                                // stayInFrame
                                key={uniqueId(eventInfo[index]?.id + index)}
                                allowFlip
                                open={false} // Disable popover to prevent conflicts with our slide-in
                                // placement='top'
                              >
                                <Popover.Trigger asChild style={{ flex: 1, alignSelf: "stretch" }}>
                                  {
                                    (marking as any).type === 'social' ?
                                      <div
                                        draggable={!isDragging}
                                        onDragStart={(event: React.DragEvent<HTMLDivElement>) => {
                                          if (isDragging) {
                                            event.preventDefault();
                                            event.stopPropagation();
                                          }
                                        }}
                                        style={{
                                          pointerEvents: isDragging ? 'none' : 'auto',
                                        }}
                                      >
                                        <Pressable 
                                          onPress={() => {
                                            console.log('Social event pressed:', eventInfo[index]);
                                            onEventClick?.(eventInfo[index]);
                                          }}
                                          onLongPress={() => onEventClick?.(eventInfo[index])}
                                          style={{ 
                                            shadowRadius: 6, 
                                            shadowOpacity: 0.1, 
                                            backgroundColor: dot.color, 
                                            borderRadius: 8, 
                                            marginHorizontal: 2, 
                                            flex: 1, 
                                            overflow: "hidden", 
                                            ...(dot as any).customContainerStyle, 
                                            minHeight: 28, 
                                            borderWidth: 1, 
                                            borderColor: 'rgba(255, 255, 255, 0.15)',
                                            shadowColor: '#000',
                                            shadowOffset: { width: 0, height: 1 },
                                            opacity: hasBlockedEvent ? 0.4 : 1,
                                          }} 
                                        >
                                        <SocialEventMarking eventInfo={eventInfo} dot={dot} index={index} />
                                      </Pressable>
                                      </div>
                                      :
                                      <Pressable 
                                        pointerEvents={isDragging ? 'none' : 'auto'}
                                        onPress={() => {
                                          console.log('Regular event pressed:', eventInfo[index]);
                                          onEventClick?.(eventInfo[index]);
                                        }}
                                        onLongPress={() => onEventClick?.(eventInfo[index])}
                                        style={{ 
                                          shadowRadius: 6, 
                                          shadowOpacity: 0.1, 
                                          backgroundColor: dot.color, 
                                          borderRadius: 8, 
                                          marginHorizontal: 2, 
                                          flex: 1, 
                                          overflow: "hidden", 
                                          ...(dot as any).customContainerStyle, 
                                          minHeight: 28, 
                                          borderWidth: 1, 
                                          borderColor: 'rgba(255, 255, 255, 0.15)',
                                          shadowColor: '#000',
                                          shadowOffset: { width: 0, height: 1 },
                                          opacity: hasBlockedEvent ? 0.4 : 1,
                                        }} 
                                      >
                                        <View style={{
                                          flex: 1,
                                          alignSelf: "stretch",
                                          flexDirection: 'row',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          paddingHorizontal: 4
                                        }}>
                                          {eventInfo[index]?.user && (
                                            <UserAvatar user={eventInfo[index].user} size={16} />
                                          )}
                                          <SizableText 
                                            userSelect='none' 
                                            style={{ 
                                              justifyContent: "center", 
                                              verticalAlign: "middle", 
                                              textAlign: "center", 
                                              fontWeight: '600', 
                                              color: 'white', 
                                              fontSize: 10, 
                                              fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
                                              flex: 1,
                                              ...(dot as any).customTextStyle 
                                            }}
                                            numberOfLines={1}
                                            ellipsizeMode="tail"
                                          >
                                            {eventInfo[index]?.title || ""}
                                          </SizableText>
                                    </View>
                                  </Pressable>
                                  }
                                </Popover.Trigger>
                                <Popover.Content
                                  enterStyle={{ y: -10, opacity: 0 }}
                                  exitStyle={{ y: -10, opacity: 0 }}
                                  elevate
                                  animation={[
                                    'quick',
                                    {
                                      opacity: {
                                        overshootClamping: true,
                                      },
                                    },
                                  ]}
                                  style={{
                                    overflow: "visible",
                                    shadowRadius: 16,
                                    shadowOpacity: theme ? 0.15 : 0.25,
                                    shadowColor: theme ? "rgba(0, 0, 0, 0.1)" : "rgba(0, 0, 0, 0.4)",
                                    backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)',
                                    top: -10,
                                    padding: 16,
                                    minWidth: 320,
                                    minHeight: 280,
                                    borderRadius: 16,
                                    borderWidth: 1,
                                    borderColor: theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
                                  }}
                                >{
                                    (marking as any).type === 'social' ? <SocialEventContent eventInfo={eventInfo} index={index} /> :
                                      <YStack flex={1} paddingHorizontal={16} alignSelf='stretch' gap={"$2"}>
                                        <Text style={{ fontSize: 20, fontWeight: '600', lineHeight: 24, color: theme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)' }}>{eventInfo[index]?.title || ""}</Text>
                                        <Separator backgroundColor={theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'} />
                                    {
                                          eventInfo[index]?.allDay ? <Text style={{ color: theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)' }}>All Day</Text> : (
                                        <XStack alignSelf={"stretch"} flex={1} alignContent='space-around'>
                                          <View style={{ flex: 1 }}>
                                                <Label style={{ color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)' }}>Starts</Label>
                                                <Text style={{ flex: 1, color: theme ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)' }}>{moment(eventInfo[index]?.startDate).format('HH:MMa').toString()}</Text>
                                          </View>
                                          <View style={{ flex: 1, alignItems: 'flex-end', alignContent: 'flex-end' }}>
                                                <Label textAlign='right' style={{ color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)' }}>Ends</Label>
                                                <Text style={{ flex: 1, textAlign: "right", color: theme ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)' }}>{moment(eventInfo[index]?.endDate).format('HH:MMa').toString()}</Text>
                                          </View>
                                        </XStack>
                                      )
                                    }
                                        <Text style={{ color: theme ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)' }}>📍 {eventInfo[index]?.location}</Text>
                                        <Text style={{ color: theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)' }}>{eventInfo[index]?.description}</Text>
                                        <Separator backgroundColor={theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'} />
                                    {
                                      eventInfo[index]?.attendees?.map((attendee, index) => (
                                        <Text key={index} style={{ color: theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)' }}>{attendee}</Text>
                                      ))
                                    }
                                        <YStack gap={"$2"}>
                                          <Label style={{ color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)' }}>Notes</Label>
                                      <TextArea
                                        value={eventInfo[index]?.notes || undefined}
                                        editable={true}
                                        multiline={true}
                                            backgroundColor={theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)'}
                                            borderColor={theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'}
                                            borderRadius={8}
                                      />
                                    </YStack>
                                    {
                                      eventInfo[index]?.attachments?.map((attachment, index) => (
                                        <Text key={index} style={{ color: theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)' }}>{attachment}</Text>
                                      ))
                                    }
                                  </YStack>
                                  }
                                </Popover.Content>
                                <Popover.Arrow borderWidth={1} borderColor="$borderColor" />
                              </Popover>
                            )
                          })}
                        </ScrollView>
                      </Animatable.View>
                    </LinearGradient>
                  </ErrorBoundary>
                </div>
            ), [date, marking, markingType, isInDragRange, draggedOver, isDragging]);
          }}
          theme={{
            backgroundColor: theme ? 'white' : 'grey',
            calendarBackground: "transparent",
            textSectionTitleColor: 'white',
            selectedDayBackgroundColor: 'rgb(90,192,234)',
            selectedDayTextColor: 'white',
            agendaTodayColor: 'rgb(90,192,234)',
            agendaDayTextColor: theme ? "black" : "white",
            todayTextColor: 'white',
            textDayStyle: { textAlign: "right", color: theme ? "black" : "white" },
            dayTextColor: theme ? 'black' : 'white',
            textDisabledColor: 'grey',
            dotColor: 'white',
            selectedDotColor: 'white',
            arrowColor: 'white',
            monthTextColor: 'white',
            indicatorColor: 'white',
            // textDayFontFamily: 'montserrat',
            // textMonthFontFamily: 'montserrat',
            // textDayHeaderFontFamily: 'montserrat',
            textDayFontWeight: '400',
            textMonthFontWeight: 'bold',
            textDayHeaderFontWeight: 'bold',
            textDayFontSize: 12,
            textMonthFontSize: 16,
            timelineContainer: { backgroundColor: theme ? 'white' : 'grey' },
            contentStyle: { backgroundColor: theme ? 'white' : 'grey' },
            textDayHeaderFontSize: 14,
            todayBackgroundColor: theme ? 'rgb(90,192,234)' : 'grey',
            stylesheet: {
              calendar: {
                main: {
                  current: {
                    backgroundColor: 'red',
                    borderRadius: 16,
                    contentStyle: { width: 20 }
                  },
                },
                header: {
                  fontWeight: 'bold',
                  fontFamily: 'montserrat',
                  numberOfLines: 1,
                  week: {
                    padding: 1,
                    textAlign: "center",
                    minHeight: 60,
                    overflow: "visible",
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  },
                },
              },
              day: {
                basic: {
                  height: 140,
                  width: 70,
                }
              }
            },
          }}
          markedDates={finalMarked}
          allowShadow
          hideArrows
          customHeaderTitle={(<></>)}
          closeOnDayPress={false}
          contentContainerStyle={{ flex: 1, backgroundColor: 'white' }}
            headerStyle={{
              backgroundColor: theme ? 'rgba(90, 192, 234, 0.95)' : 'rgba(60, 60, 60, 0.95)',
              padding: Platform.OS === 'web' ? 1 : undefined,
              shadowRadius: 16,
              borderTopStartRadius: 16,
              borderTopEndRadius: 16,
              borderBottomStartRadius: 16,
              borderBottomEndRadius: 16,
              elevation: 12,
              shadowOpacity: theme ? 0.15 : 0.25,
              shadowColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.4)',
              shadowOffset: { width: 0, height: 4 },
              borderWidth: 1,
              borderColor: theme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
            }}
          calendarWidth={isTablet ? calendarWidth * 1.03 : calendarWidth * 1.07}
          onCalendarToggled={(isOpen) => { setIsOpen(isOpen); }}
          showWeekNumbers={false}
          onDayLongPress={_handleLongPress}
            style={{ flex: 10 }}
          columnWrapperStyle={{ flex: 1, justifyContent: 'space-between' }}
          onDayPress={event => {
            // console.log(event, 'event');
            _handleDayPress(event, event.dateString);
          }}
          hideKnob
          markingType={'multi-period'}
        />
        </ErrorBoundary>
      </CalendarProvider>
    </View>
  ), [selectedDate, finalMarked, calHeight, eventList, calLoading, theme, isDragging, dragStartDate, dragEndDate, showDateRangeModal, selectedDateRange, isBlockingDates, isCreatingMultiDayEvent]);
});

export default CalendarComponent;
